import React, { useState, ChangeEvent, FormEvent } from 'react';
import { FormData, FormValidationErrors } from '../Types';
import { translations } from '../translations';
import Select from 'react-select'

import './ApplicationForm.css';

interface ApplicationFormProps {
    onSubmit: (data: FormData) => void;
    language: 'en' | 'fi';
}

interface CheckboxProps {
    label: string;
    value: string;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

const Checkbox: React.FC<CheckboxProps> = ({ label, value, onChange }) => (
    <div className="Checkbox">
        <input type="checkbox" value={value} onChange={onChange} id={value} className="Checkbox-input" />
        <label htmlFor={value} className="Checkbox-label">{label}</label>
    </div>
);

const ApplicationForm: React.FC<ApplicationFormProps> = ({ onSubmit, language }) => {
    const [formData, setFormData] = useState<FormData>({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        gender: '',
        organization: '',
        fieldOfStudy: '',
        workingLocations: [],
        studyYear: '',
        typeOfEmployment: [],
        applicationDate: '',
        resume: null,
        coverLetter: null,
        openPositions: [],
        message: '',
    });

    const schools = [
        'Aalto-yliopisto', 'Åbo akademi', 'Arkada', 'Centria ammattikorkeakoulu', 'HAAGA-HELIA ammattikorkeakoulu', 'Hämeen ammattikorkeakoulu',
        'Hanken Svenska handelshögskolan', 'Helsingin yliopisto', 'Itä-suomen yliopisto', 'Jyväskylän ammattikorkeakoulu', 'Jyväskylän yliopisto',
        'Kaakkois-Suomen ammattikorkeakoulu', 'Kajaanin ammattikorkeakoulu', 'Karelia ammattikorkeakoulu', 'LAB-ammattikorkeakoulu', 'Lapin ammattikorkeakoulu',
        'Lapin yliopisto', 'Laurea-ammattikorkeakoulu', 'LUT-yliopisto', 'Metropolia ammattikorkeakoulu', 'Oulun ammattikorkeakoulu', 'Oulun yliopisto',
        'Seinäjoen ammattikorkeakoulu', 'Tampereen ammattikorkeakoulu', 'Tampereen yliopisto', 'Turun ammattikorkeakoulu', 'Turun yliopisto',
        'Vaasan ammattikorkeakoulu', 'Vaasan yliopisto'
    ];

    const workingLocations = [
        'Helsinki', 'Espoo', 'Jyväskylä', 'Lahti', 'Lappeenranta', 'Oulu', 'Tampere', 'Turku', 'Vaasa', 'Vantaa'
    ];

    const employmentTypes = [
        { key: 'fullTime', label: translations[language].fullTime },
        { key: 'partTime', label: translations[language].partTime },
        { key: 'summerJob', label: translations[language].summerJob },
        { key: 'trainee', label: translations[language].trainee }
    ];

    const openPositions = [
        { key: 'Komma-Espoo', label: '1Komma5 (Asiakashankkija - Espoo)' },
        { key: 'Komma-Tampere', label: '1Komma5 (Asiakashankkija - Tampere)' },
        { key: 'Komma-Turku', label: '1Komma5 (Asiakashankkija - Turku)' },
        { key: 'Webso', label: 'Webso (Myyntiedustaja)' },
    ];

    const [validationErrors, setValidationErrors] = useState<FormValidationErrors>({});

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>, field: keyof FormData) => {
        const { value, checked } = e.target;
        let updatedArray = [...(formData[field] as string[])];

        if (checked) {
            updatedArray.push(value);
        } else {
            updatedArray = updatedArray.filter(item => item !== value);
        }

        setFormData({
            ...formData,
            [field]: updatedArray,
        });
    };


    const handleFileChange = (e: ChangeEvent<HTMLInputElement>, fieldName: keyof FormData) => {
        const file = e.target.files ? e.target.files[0] : null;
        setFormData({
            ...formData,
            [fieldName]: file,
        });
    };

    const isValidForm = () => {
        const errors: FormValidationErrors = {};

        if (!formData.firstName) errors.firstName = "Required";
        if (!formData.lastName) errors.lastName = "Required";
        if (!formData.email) errors.email = "Required";
        if (!formData.resume) errors.resume = "Required";

        setValidationErrors(errors);

        return Object.keys(errors).length === 0;
    };

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!isValidForm()) {
            return;
        }

        onSubmit(formData);
    };

    return (
        <div className="ApplicationForm">
            <form onSubmit={handleSubmit}>
                <div className={validationErrors.firstName ? "input-error" : ""}>
                    <label>{translations[language].firstName}<span className="required-marker">*</span></label>
                    <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                    />
                </div>
                <div className={validationErrors.firstName ? "input-error" : ""}>
                    <label>{translations[language].lastName}<span className="required-marker">*</span></label>
                    <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                    />
                </div>
                <div className={validationErrors.email ? "input-error" : ""}>
                    <label>{translations[language].email}<span className="required-marker">*</span></label>
                    <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                    />
                </div>
                <div>
                    <label>{translations[language].phoneNumber}</label>
                    <input
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleChange}
                    />
                </div>

                <div>
                    <label>{translations[language].organization}</label>
                    <Select options={schools.map(school => ({ value: school, label: school }))} />
                </div>
                <div>
                    <label>{translations[language].fieldOfStudy}</label>
                    <input
                        type="study"
                        name="fieldOfStudy"
                        value={formData.fieldOfStudy}
                        onChange={handleChange}
                    />
                </div>
                <div>
                    <label>{translations[language].studyYear}</label>
                    <Select options={[
                        { value: '1', label: '1' },
                        { value: '2', label: '2' },
                        { value: '3', label: '3' },
                        { value: '4', label: '4' },
                        { value: '5', label: '5' },
                        { value: '5+', label: '5+' },
                    ]} />
                </div>
                <div>
                    <label>{translations[language].workingLocations}</label>
                    <Select options={workingLocations.map(location => ({ value: location, label: location }))} isMulti />
                </div>
                <div>
                    <label>{translations[language].typeOfEmployment}</label>
                    <div className="checkbox-field">
                        {employmentTypes.map(type => (
                            <Checkbox
                                key={type.key}
                                label={type.label}
                                value={type.key}
                                onChange={(e) => handleCheckboxChange(e, 'typeOfEmployment')}
                            />
                        ))}
                    </div>
                </div>
                <div>
                    <label className="custom-label">{translations[language].applicationDate}</label>
                    <input type="date" name="applicationDate" value={formData.applicationDate} onChange={handleChange} className="custom-input" />
                </div>
                <div className={validationErrors.resume ? "input-error" : ""}>
                    <label>{translations[language].resume}<span className="required-marker">*</span></label>
                    <input className="file-input-container"
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={e => handleFileChange(e, 'resume')}
                    />
                </div>
                <div>
                    <label>{translations[language].coverLetter}</label>
                    <input className="file-input-container"
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={e => handleFileChange(e, 'coverLetter')}
                    />
                </div>
                {/*  <div>
                    <label>{translations[language].openPositions}</label>
                    <div className="checkbox-field">
                        {openPositions.map(type => (
                            <Checkbox
                                key={type.key}
                                label={type.label}
                                value={type.key}
                                onChange={(e) => handleCheckboxChange(e, 'openPositions')}
                            />
                        ))}
                    </div>
                </div> */}
                <div>
                    <label className="custom-label">{translations[language].message}</label>
                    <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        className="custom-textarea"
                    />
                </div>
                <div className='gdpa-label'>
                    <label>{translations[language].gdpaLabel}</label>
                    <a href='https://www.nemora.io/tietosuojaseloste'>
                        <span>{translations[language].gdpaLink}</span>
                    </a>
                </div>
                <button type="submit">{translations[language].submit}</button>
            </form>
        </div>
    );
};

export default ApplicationForm;
